// Ship Gate 2b — TurboTax-style global Tax Year selector.
// All financial pages (Dashboard, Income, Expenses, Tax Prep, Equipment, Grant Tracking)
// read the active tax year from here. CACFP pages have their own per-page FY picker
// because CACFP fiscal year is independent (calendar OR USDA Oct–Sept).
//
// Default Tax Year Resolver (May 2026): the dropdown defaults to the backend's
// `default_tax_year` (prior completed year — what users are filing), never the
// in-progress calendar year. The backend is the authoritative source so preview
// and production behave identically.

import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "sst_active_tax_year";

// Safe client-side default if the API hasn't responded yet — matches the
// backend's DEFAULT_LAUNCH_TAX_YEAR so first paint never shows the wrong year.
const CLIENT_DEFAULT_TAX_YEAR = 2025;

const TaxYearContext = createContext(null);

export function TaxYearProvider({ children }) {
  // Hydrate from localStorage so the user's selection persists across reloads.
  // Fallback to CLIENT_DEFAULT_TAX_YEAR (NOT the JS calendar year) so users
  // never see the in-progress year flash before the API responds.
  const [taxYear, setTaxYearState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const parsed = stored ? parseInt(stored, 10) : NaN;
      if (Number.isFinite(parsed)) {
        return parsed;
      }
      return CLIENT_DEFAULT_TAX_YEAR;
    } catch {
      return CLIENT_DEFAULT_TAX_YEAR;
    }
  });
  const [availableYears, setAvailableYears] = useState([CLIENT_DEFAULT_TAX_YEAR]);
  // Stage 3B — backend-authoritative per-year can_view/can_write status
  // ({tax_year, entitlement_state, can_view, can_write, reason}). TopBar and
  // the Vault processing modal read this instead of re-deriving access.
  const [yearStatus, setYearStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [defaultTaxYear, setDefaultTaxYearState] = useState(CLIENT_DEFAULT_TAX_YEAR);
  const [backendCurrentYear, setBackendCurrentYear] = useState(CLIENT_DEFAULT_TAX_YEAR);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/tax-years`, { withCredentials: true });
      const apiDefault = data.default_tax_year || CLIENT_DEFAULT_TAX_YEAR;
      const apiCurrent = data.current_tax_year || apiDefault;
      const years = Array.isArray(data.years) && data.years.length ? data.years : [apiDefault];
      setAvailableYears(years);
      setYearStatus(Array.isArray(data.year_status) ? data.year_status : []);
      setDefaultTaxYearState(apiDefault);
      setBackendCurrentYear(apiCurrent);
      // If the persisted selection is no longer valid, fall back to backend's default.
      // Also: if user has NO localStorage choice, trust the backend's default.
      let hasStored = false;
      try { hasStored = !!localStorage.getItem(STORAGE_KEY); } catch { /* no-op */ }
      if (!years.includes(taxYear)) {
        setTaxYearState(apiDefault);
      } else if (!hasStored && taxYear !== apiDefault) {
        setTaxYearState(apiDefault);
      }
    } catch {/* silent — keep defaults */}
    finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const setTaxYear = useCallback((y) => {
    const n = parseInt(y, 10);
    if (!Number.isFinite(n)) return;
    setTaxYearState(n);
    try { localStorage.setItem(STORAGE_KEY, String(n)); } catch {/* no-op */}
    // Notify any listening pages that should refetch.
    try { window.dispatchEvent(new CustomEvent("tax-year:changed", { detail: { taxYear: n } })); } catch {/* no-op */}
  }, []);

  // Looks up ONE year's backend-authoritative status. Falls back to a fully
  // open status only while yearStatus hasn't loaded yet, so the UI never
  // flashes "Locked" before the real API response arrives.
  const getYearStatus = useCallback((y) => {
    const found = yearStatus.find((s) => s.tax_year === y);
    if (found) return found;
    return { tax_year: y, entitlement_state: null, can_view: true, can_write: true, reason: null };
  }, [yearStatus]);

  return (
    <TaxYearContext.Provider value={{
      taxYear,
      setTaxYear,
      availableYears,
      yearStatus,
      getYearStatus,
      loading,
      refresh,
      defaultTaxYear,
      // currentCalendarYear is retained for backward compat — many components
      // still import this name. It now reflects the backend's authoritative
      // "default tax year" so customer-facing copy stays correct.
      currentCalendarYear: defaultTaxYear,
      // backendCurrentYear is the raw `current_tax_year` from the API (whatever
      // current_tax_year() returns — may differ from defaultTaxYear if an
      // env override is set). Internal use only.
      backendCurrentYear,
    }}>
      {children}
    </TaxYearContext.Provider>
  );
}

export function useTaxYear() {
  const ctx = useContext(TaxYearContext);
  if (!ctx) {
    // Defensive default — should never happen if provider is mounted.
    const y = CLIENT_DEFAULT_TAX_YEAR;
    return {
      taxYear: y,
      setTaxYear: () => {},
      availableYears: [y],
      yearStatus: [],
      getYearStatus: () => ({ tax_year: y, entitlement_state: null, can_view: true, can_write: true, reason: null }),
      loading: false,
      refresh: () => {},
      defaultTaxYear: y,
      currentCalendarYear: y,
      backendCurrentYear: y,
    };
  }
  return ctx;
}
